<template>
    <div>
        <div class="device-info">
            <div class="flex-list">
                <div class="flex-list-item" v-if="device.registrationId">
                    <i class="fas fa-car label-icon"
                       :title="$t('info.license_plate_number')">
                    </i>
                    <span>{{ device.registrationId }}</span>
                </div>
                <div class="flex-list-item" v-if="device.speed || device.speed === 0">
                    <i class="fas fa-tachometer-alt label-icon"
                       :title="$t('info.current_velocity')">
                    </i>
                    <span>{{ device.speed }} km/h</span>
                </div>
                <div class="flex-list-item" v-if="device.alt || device.speed === 0">
                    <i class="fas fa-mountain label-icon"
                       :title="$t('info.elevation')"></i>
                    <span>{{ device.alt }} m</span>
                </div>
                <div class="flex-list-item" v-if="device.battery || device.batteryWarning">
                    <i class="fas fa-battery-half label-icon"
                       :title="$t('info.battery_charge')">
                    </i>
                    <span>{{ device.battery }}%</span>
                </div>
            </div>
            <div class="vr"></div>
            <div class="toolbar-button"
                 @click="toggleFollow"
                 :class="{ active: follow.id && follow.id === device.id }"
                 :title="$t('info.follow_device')">
                <i class="fas fa-crosshairs"></i>
            </div>
        </div>

        <hr/>
        <div class="fill-height">
            <div class="grows">
                <div class="flex-row-list">
                    <div class="info-row" v-if="device.drivers && device.drivers.length > 0">
                        <i class="fas fa-user label-icon"></i>
                        <span>{{ device.drivers[0] }}</span>
                    </div>
                    <div class="info-row">
                        <i class="fas fa-clock label-icon" :title="$t('info.last_location_time')"></i>
                        <span class="last-seen" :title="device.lastTime | duration('humanize', true)">
                            {{ device.lastTime | moment("HH:mm:ss, ddd DD. M. YYYY") }}
                        </span>
                    </div>
                    <div class="info-row" v-if="device.address">
                        <i class="fas fa-home label-icon" :title="$t('info.last_street_address')"></i>
                        <div class="text-inline-block">
                            <span>
                                {{device.address}}
                                <a class="share-icon" target="_blank"
                                   :href="'https://www.google.com/maps/search/?api=1&query='+encodeURI(device.address)"
                                   :title="$t('info.open_google_maps')">
                                    <i class="fas fa-external-link-alt fa-sm"></i>
                                </a>
                            </span>
                        </div>
                    </div>
                    <div class="info-row" v-if="device.position">
                        <i class="fas fa-map-pin label-icon" :title="$t('info.last_coordinates')"></i>
                        <div class="text-inline-block">
                            <span>
                                {{ device.dms }}
                                <a class="share-icon" target="_blank"
                                   :href="'https://www.google.com/maps/place/'+device.position"
                                   :title="$t('info.open_google_maps')">
                                    <i class="fas fa-external-link-alt fa-sm"></i>
                                </a>
                            </span>
                        </div>
                        <a class="share-icon" v-if="canShare" @click="share({'text': device.position.toString()})">
                            <i class="fas fa-share-alt fa-sm"></i>
                        </a>
                        <span v-if="canShare">&nbsp;&nbsp;</span>
                    </div>
                    <div class="info-row" v-if="device.hasKeyboard">
                        <i class="fas fa-keyboard label-icon" :title="$t('info.last_coordinates')"></i>
                        <div class="text-inline-block">
                            <span>
                                 <span>{{ $t('keyboard.status') }}</span>
                                ( <span>{{ $t('keyboard.last_updated') }}: {{ device.keyboardStatusForDisplay.timestamp | moment("DD.MM.YYYY, HH:mm:ss") }} </span>)

                                 <div class="keyboard-container">
                                      <div
                                          class="keyboard-key"
                                          v-for="index in [1, 2, 3, 4, 5, 6]"
                                          :key="index"
                                          :class="{
                                          'pressed': getKeyByIndex(index).pressed === true,
                                          'not-pressed': getKeyByIndex(index).pressed === false,
                                          'intermediate-state': device.keyboardStatusForDisplay === null
                                        }"
                                      >
                                        {{ index }}
                                      </div>
                                 </div>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Info',
    methods: {
        share(data) {
            navigator.share(data);
        },
        toggleFollow() {
            if (this.follow.id !== this.device.id) {
                this.$store.commit('map/followDevice', this.device);
            } else {
                this.$store.commit('map/followDevice', -1);
            }
        },
        getKeyByIndex(index) {
            const keys = this.device.keyboardStatusForDisplay.keys;
            return keys.find((key) => key.id === index);
        }
    },
    computed: {
        device: function() {
            return this.$store.getters.selectedDevice;
        },
        elapsedTime: function() {
            return this.$moment(this.device.lastTime).diff(this.$moment());
        },
        canShare: function() {
            return navigator.share;
        },
        selectedDevice: function() {
            return this.$store.getters.selectedDevice;
        },
        follow: function() {
            return this.$store.state.map.followDevice;
        }
    }
};
</script>

<style lang="scss">
    .flex-list {
        flex: 1 1 auto;
    }

    .flex-list,
    .flex-row-list {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: center;

        .share-icon {
            margin-left: 0.5em;
        }

        .info-row {
            margin: 0.2em 0;
            width: 100%;
            color: $off-white;
        }

        .label-icon {
            font-size: 0.9em;
            text-align: center;
            margin-right: 0.5em;
            min-width: 1.1em;
        }

        .flex-list-item {
            margin: 0 0.2em;
        }
    }

    .fill-height {
        display: flex;
        flex-direction: column;
    }

    .text-inline-block {
        max-width: 90%;
        display: inline-flex;
        margin-left: 0.2em;
    }

    .keyboard-container {
        display: flex;
        flex-wrap: wrap;
    }

    .keyboard-key {
        width: 20px;
        height: 20px;
        margin: 5px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 14px;
        cursor: pointer;
        transition: background-color 0.2s ease;
    }

    .pressed {
        background-color: #4CAF50;
    }

    .not-pressed {
        background-color: #f1504e;
    }

    .intermediate-state {
        background-color: grey;
        color: black;
    }
</style>
